import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  Typography,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Block as BlockIcon,
  Save as SaveIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ordenCompraService, { OrdenCompra } from '../../services/ordenCompraService';
import ubicacionService from '../../services/ubicacionService';
import { formatCurrency, formatDateLocal } from '../../utils/formatters';
import {
  esOrdenCompraAnulada,
  toFechaVencimInputValue,
  ymdToDdMmYyyy,
} from '../../utils/ordenCompraFechaProbable';
import NumeroInternoMeta from '../../components/NumeroInternoMeta';
import { getApiErrorMessage } from '../../utils/apiError';

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  const s = formatDateLocal(fecha);
  return s || '-';
};

const formatoLetraSucNro = (letra?: string, nroSuc?: number, nroComprobante?: number): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(4, '0');
  const nro = nroComprobante ?? 0;
  return `${L}-${suc}-${String(nro).padStart(8, '0')}`;
};

const OrdenCompraDetalle: React.FC = () => {
  const navigate = useNavigate();
  const { nroFormulario } = useParams<{ nroFormulario: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const datePickerRef = useRef<HTMLInputElement>(null);

  const [orden, setOrden] = useState<OrdenCompra | null>(null);
  const [ubicacionDestinoNombre, setUbicacionDestinoNombre] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [anulando, setAnulando] = useState(false);
  const [fechaVencimEdit, setFechaVencimEdit] = useState('');
  const [fechaVencimBase, setFechaVencimBase] = useState('');
  const [guardandoFecha, setGuardandoFecha] = useState(false);

  const loadOrden = useCallback(async () => {
    const id = parseInt(nroFormulario || '', 10);
    if (Number.isNaN(id)) {
      enqueueSnackbar('Orden de compra no válida', { variant: 'error' });
      navigate('/compras/ordenes');
      return;
    }

    try {
      setLoading(true);
      const data = await ordenCompraService.getOrdenCompra(id);
      setOrden(data);
      const ymd = toFechaVencimInputValue(data.fechaVencim);
      setFechaVencimEdit(ymd);
      setFechaVencimBase(ymd);

      if (data?.nroUbicacionDestino) {
        try {
          const u = await ubicacionService.getUbicacion(data.nroUbicacionDestino);
          setUbicacionDestinoNombre(u?.nombreUbicacion?.trim() || '');
        } catch {
          setUbicacionDestinoNombre('');
        }
      } else {
        setUbicacionDestinoNombre('');
      }
    } catch (error) {
      console.error('Error al cargar orden de compra:', error);
      enqueueSnackbar('Error al cargar la orden de compra', { variant: 'error' });
      navigate('/compras/ordenes');
    } finally {
      setLoading(false);
    }
  }, [nroFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    loadOrden();
  }, [loadOrden]);

  const openDatePicker = () => {
    const el = datePickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === 'function') {
      try {
        el.showPicker();
        return;
      } catch {
        // fallback click
      }
    }
    el.click();
  };

  const handleAnular = async () => {
    if (!orden || anulando || esOrdenCompraAnulada(orden)) return;
    try {
      const check = await ordenCompraService.puedeAnularOrdenCompra(orden.nroFormulario);
      if (!check.puedeAnular) {
        enqueueSnackbar(check.motivos[0] ?? 'No se puede anular esta orden de compra', { variant: 'warning' });
        return;
      }
      if (!window.confirm('¿Está seguro que desea anular esta orden de compra?')) return;

      setAnulando(true);
      await ordenCompraService.anularOrdenCompra(orden.nroFormulario);
      enqueueSnackbar('Orden de compra anulada correctamente', { variant: 'success' });
      await loadOrden();
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al anular la orden de compra'), { variant: 'error' });
    } finally {
      setAnulando(false);
    }
  };

  const handleGuardarFechaVencim = async () => {
    if (!orden || guardandoFecha || esOrdenCompraAnulada(orden)) return;
    if (!fechaVencimEdit) {
      enqueueSnackbar('Ingrese la fecha probable de entrega', { variant: 'warning' });
      return;
    }
    const fechaEmisionYmd = toFechaVencimInputValue(orden.fechaEmision);
    if (fechaEmisionYmd && fechaVencimEdit < fechaEmisionYmd) {
      enqueueSnackbar('La fecha probable de entrega no puede ser anterior a la fecha de emisión', {
        variant: 'warning',
      });
      return;
    }
    if (fechaVencimEdit === fechaVencimBase) return;

    try {
      setGuardandoFecha(true);
      const updated = await ordenCompraService.actualizarFechaVencim(orden.nroFormulario, fechaVencimEdit);
      if (updated && typeof updated === 'object' && 'nroFormulario' in updated) {
        setOrden(updated);
        const ymd = toFechaVencimInputValue(updated.fechaVencim);
        setFechaVencimEdit(ymd);
        setFechaVencimBase(ymd);
      } else {
        await loadOrden();
      }
      enqueueSnackbar('Fecha probable de entrega actualizada', { variant: 'success' });
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 405) {
        enqueueSnackbar(
          'La API no tiene el endpoint para guardar la fecha (PATCH .../fecha-vencim). Pedile a backend que lo habilite.',
          { variant: 'error', autoHideDuration: 8000 },
        );
      } else {
        enqueueSnackbar(getApiErrorMessage(error, 'Error al actualizar la fecha probable de entrega'), {
          variant: 'error',
        });
      }
    } finally {
      setGuardandoFecha(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#1976d2' }} />
      </Box>
    );
  }

  if (!orden) return null;

  const anulada = esOrdenCompraAnulada(orden);
  const fechaDirty = !anulada && fechaVencimEdit !== fechaVencimBase;
  const fechaEmisionMin = toFechaVencimInputValue(orden.fechaEmision);

  const handleFechaVencimChange = (ymd: string) => {
    if (ymd && fechaEmisionMin && ymd < fechaEmisionMin) {
      enqueueSnackbar('La fecha probable de entrega no puede ser anterior a la fecha de emisión', {
        variant: 'warning',
      });
      return;
    }
    setFechaVencimEdit(ymd);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/compras/ordenes')}
              sx={{ textTransform: 'none' }}
            >
              Órdenes de Compra
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2', flex: 1 }}>
              Orden de Compra {formatoLetraSucNro(orden.letra, orden.nroSuc, orden.nroComprobante ?? undefined)}
              {anulada && (
                <Chip label="Anulada" size="small" color="default" variant="outlined" sx={{ ml: 1, verticalAlign: 'middle' }} />
              )}
            </Typography>
            {!anulada && (
              <Button
                variant="outlined"
                color="error"
                startIcon={anulando ? <CircularProgress size={18} /> : <BlockIcon />}
                onClick={handleAnular}
                disabled={anulando}
                sx={{ textTransform: 'none', alignSelf: { xs: 'stretch', sm: 'center' } }}
              >
                Anular
              </Button>
            )}
          </Stack>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Encabezado
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Fecha emisión
              </Typography>
              <Typography variant="body1">{formatearFecha(orden.fechaEmision)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={1}>
                {anulada ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Fecha probable de entrega
                    </Typography>
                    <Typography variant="body1">
                      {formatearFecha(orden.fechaVencim)}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Box sx={{ position: 'relative' }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Fecha probable de entrega"
                        value={fechaVencimEdit ? ymdToDdMmYyyy(fechaVencimEdit) : ''}
                        placeholder="DD/MM/AAAA"
                        onClick={openDatePicker}
                        inputProps={{
                          readOnly: true,
                          style: { cursor: 'pointer' },
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={openDatePicker}
                                aria-label="Seleccionar fecha"
                              >
                                <CalendarMonthIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <input
                        ref={datePickerRef}
                        type="date"
                        value={fechaVencimEdit}
                        min={fechaEmisionMin || undefined}
                        onChange={(e) => handleFechaVencimChange(e.target.value)}
                        style={{
                          position: 'absolute',
                          opacity: 0,
                          width: 0,
                          height: 0,
                          pointerEvents: 'none',
                        }}
                        tabIndex={-1}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={guardandoFecha ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      onClick={handleGuardarFechaVencim}
                      disabled={!fechaDirty || guardandoFecha}
                      sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                    >
                      Guardar fecha
                    </Button>
                  </>
                )}
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Comprobante
              </Typography>
              <Typography variant="body1">
                {formatoLetraSucNro(orden.letra, orden.nroSuc, orden.nroComprobante ?? undefined)}
              </Typography>
            </Grid>
            {orden.ordenDeCompra ? (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Orden de compra (ref.)
                </Typography>
                <Typography variant="body1">{orden.ordenDeCompra}</Typography>
              </Grid>
            ) : null}
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Proveedor
              </Typography>
              <Typography variant="body1">
                {orden.nroProveedor != null ? orden.nroProveedor : '-'} - {orden.nombre || '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                CUIT
              </Typography>
              <Typography variant="body1">{orden.cuit || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Ubicación destino
              </Typography>
              <Typography variant="body1">
                {orden.nroUbicacionDestino != null
                  ? ubicacionDestinoNombre
                    ? `${orden.nroUbicacionDestino} - ${ubicacionDestinoNombre}`
                    : String(orden.nroUbicacionDestino)
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Usuario
              </Typography>
              <Typography variant="body1">{orden.usuario || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Importe total
              </Typography>
              <Typography variant="body1">{formatCurrency(orden.importeTotal ?? 0)}</Typography>
            </Grid>
            {orden.descripcion ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Descripción
                </Typography>
                <Typography variant="body1">{orden.descripcion}</Typography>
              </Grid>
            ) : null}
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Detalle
          </Typography>

          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Cantidad
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    P. unitario
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Subtotal
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Notas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(orden.detalles || []).map((d, idx) => (
                  <TableRow key={`${d.nroIdformulario}-${d.nroItem}-${idx}`}>
                    <TableCell>{d.codigoArticulo || '-'}</TableCell>
                    <TableCell>{d.descripcionArticulo || '-'}</TableCell>
                    <TableCell align="right">
                      {Number(d.cantidad)?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) ?? '-'}
                    </TableCell>
                    <TableCell align="right">{formatCurrency(d.precioUnitario ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(d.subtotal ?? 0)}</TableCell>
                    <TableCell>{d.notas || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <NumeroInternoMeta nro={orden.nroFormulario} />
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrdenCompraDetalle;
